import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSettings } from './Settingscontext';
import { useSound } from '../../hooks/useSound';
import './settings.css';

const NAV_LINKS = [
  { label: 'HOME',       icon: '⌂', path: '/splash' },
  { label: 'LEVELS',     icon: '◈', path: '/levels' },
  { label: 'CHARACTERS', icon: '◉', path: '/characters' },
];

export default function Settings() {
  const navigate = useNavigate();
  const {
    isOpen, closeSettings,
    volume, setVolume,
    sfxEnabled, setSfxEnabled,
    musicEnabled, setMusicEnabled,
  } = useSettings();
  const { playHover, playClick, playToggle } = useSound();
  const panelRef = useRef(null);

  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget) { playClick(); closeSettings(); }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') closeSettings(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [closeSettings]);

  useEffect(() => {
    if (isOpen && panelRef.current) panelRef.current.focus();
  }, [isOpen]);

  const go = (path) => { playClick(); closeSettings(); navigate(path); };

  const handleClose = () => { playClick(); closeSettings(); };

  const handleSfxToggle = () => {
    // play it before toggling off so the click is still audible when turning off
    if (sfxEnabled) playToggle();
    setSfxEnabled(s => !s);
    if (!sfxEnabled) setTimeout(playToggle, 0);
  };

  const handleMusicToggle = () => { playToggle(); setMusicEnabled(m => !m); };

  return (
    <div
      className={`settings-overlay ${isOpen ? 'settings-overlay--open' : ''}`}
      onClick={onBackdropClick}
      aria-hidden={!isOpen}
    >
      <div
        className="settings-panel"
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
      >
        <div className="settings-panel__header">
          <div className="settings-panel__title">
            <span className="settings-panel__title-icon">⬡</span>
            OPTIONS
          </div>
          <button className="settings-panel__close" onClick={handleClose} onMouseEnter={playHover} aria-label="Close settings">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <section className="settings-section">
          <h3 className="settings-section__label">NAVIGATE</h3>
          <div className="settings-navlinks">
            {NAV_LINKS.map(l => (
              <button key={l.path} className="settings-navlink" onClick={() => go(l.path)} onMouseEnter={playHover}>
                <span className="settings-navlink__icon">{l.icon}</span>
                <span className="settings-navlink__label">{l.label}</span>
                <svg className="settings-navlink__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </button>
            ))}
          </div>
        </section>

        <div className="settings-divider" />

        {/* MUSIC ON/OFF */}
        <section className="settings-section">
          <h3 className="settings-section__label">MUSIC</h3>
          <div className="settings-sfx">
            <span className="settings-sfx__desc">
              {musicEnabled ? 'Background music playing' : 'Background music muted'}
            </span>
            <button
              className={`settings-sfx__toggle ${musicEnabled ? 'settings-sfx__toggle--on' : ''}`}
              onClick={handleMusicToggle}
              onMouseEnter={playHover}
              aria-pressed={musicEnabled}
              aria-label="Toggle background music"
            >
              <span className="settings-sfx__knob" />
              <span className="settings-sfx__label">{musicEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>
        </section>

        <div className="settings-divider" />

        {/* VOLUME (now affects music volume) */}
        <section className="settings-section">
          <h3 className="settings-section__label">MUSIC VOLUME</h3>
          <div className="settings-volume">
            <span className="settings-volume__icon">♪</span>
            <div className="settings-volume__track">
              <input
                type="range"
                min={0} max={100}
                value={volume}
                onChange={e => setVolume(Number(e.target.value))}
                className="settings-volume__slider"
                aria-label="Music volume"
                disabled={!musicEnabled}
              />
              <div className="settings-volume__fill" style={{ width: `${volume}%` }} />
            </div>
            <span className="settings-volume__val">{volume}</span>
          </div>
        </section>

        <div className="settings-divider" />

        {/* SFX ON/OFF */}
        <section className="settings-section">
          <h3 className="settings-section__label">SOUND EFFECTS</h3>
          <div className="settings-sfx">
            <span className="settings-sfx__desc">
              {sfxEnabled ? 'Sound effects enabled' : 'Sound effects muted'}
            </span>
            <button
              className={`settings-sfx__toggle ${sfxEnabled ? 'settings-sfx__toggle--on' : ''}`}
              onClick={handleSfxToggle}
              onMouseEnter={playHover}
              aria-pressed={sfxEnabled}
              aria-label="Toggle sound effects"
            >
              <span className="settings-sfx__knob" />
              <span className="settings-sfx__label">{sfxEnabled ? 'ON' : 'OFF'}</span>
            </button>
          </div>
        </section>

        <div className="settings-panel__glow" />
      </div>
    </div>
  );
}